<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />
    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'alarms' }"
        :title="isEdit ? 'Editar grupo de classificação' : 'Novo grupo de classificação'"
        subtitle="Configure os tipos de classificação e vincule a sensores ou grupos de sensores." />

      <form class="border border-stroke bg-white p-6" @submit.prevent="save">
        <div class="space-y-6">
          <div>
            <label for="name" class="mb-1 block text-sm font-medium text-slate-700">Nome do grupo *</label>
            <input
              v-model="form.name"
              type="text"
              id="name"
              class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Ex: Linha de produção 1" />
            <p v-if="errors.name" class="mt-1 text-xs text-red-600">{{ errors.name }}</p>
          </div>

          <div class="border border-stroke bg-slate-50/50 p-4">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Tipos de classificação</h4>
            <p class="mb-3 text-xs text-slate-500">Selecione os tipos que estarão disponíveis neste grupo</p>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="ct in classificationTypes"
                :key="ct.id"
                class="flex cursor-pointer items-center gap-2 border border-stroke px-3 py-2 hover:bg-white">
                <input
                  v-model="form.classification_type_ids"
                  :value="ct.id"
                  type="checkbox"
                  class="border-stroke text-primary" />
                <span class="text-sm text-slate-700">{{ ct.name }}</span>
              </label>
            </div>
            <p v-if="!classificationTypes.length" class="text-sm text-slate-500">Crie tipos de classificação primeiro.</p>
            <p v-if="errors.types" class="mt-1 text-xs text-red-600">{{ errors.types }}</p>
          </div>

          <div>
            <h4 class="mb-2 text-sm font-semibold text-slate-800">Vincular a</h4>
            <div class="flex gap-6">
              <label class="flex cursor-pointer items-center gap-2">
                <input v-model="form.linkMode" type="radio" value="sensors" class="border-stroke text-primary" />
                <span class="text-sm text-slate-700">Sensores</span>
              </label>
              <label class="flex cursor-pointer items-center gap-2">
                <input v-model="form.linkMode" type="radio" value="groups" class="border-stroke text-primary" />
                <span class="text-sm text-slate-700">Grupos de sensores</span>
              </label>
            </div>
          </div>

          <div v-if="form.linkMode === 'sensors'" class="border border-stroke bg-slate-50/50 p-4">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Sensores</h4>
            <div class="max-h-48 space-y-2 overflow-y-auto">
              <label
                v-for="sensor in sensors"
                :key="sensor.id"
                class="flex cursor-pointer items-center gap-3 border border-transparent px-3 py-2 hover:bg-white">
                <input
                  v-model="form.sensor_ids"
                  :value="sensor.id"
                  type="checkbox"
                  class="border-stroke text-primary" />
                <span class="text-sm text-slate-700">{{ sensor.name }}</span>
              </label>
            </div>
            <p v-if="errors.link" class="mt-1 text-xs text-red-600">{{ errors.link }}</p>
          </div>

          <div v-if="form.linkMode === 'groups'" class="border border-stroke bg-slate-50/50 p-4">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Grupos de sensores</h4>
            <div class="max-h-48 space-y-2 overflow-y-auto">
              <label
                v-for="group in sensorGroups"
                :key="group.id"
                class="flex cursor-pointer items-center gap-3 border border-transparent px-3 py-2 hover:bg-white">
                <input
                  v-model="form.group_ids"
                  :value="group.id"
                  type="checkbox"
                  class="border-stroke text-primary" />
                <span class="text-sm text-slate-700">{{ group.name }}</span>
              </label>
            </div>
            <p v-if="errors.link" class="mt-1 text-xs text-red-600">{{ errors.link }}</p>
          </div>

          <div v-if="saveError" class="border border-red-200 bg-red-50 p-3 text-sm text-red-600">{{ saveError }}</div>

          <div class="flex justify-end gap-3 border-t border-stroke pt-4">
            <router-link
              :to="{ name: 'alarms' }"
              class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancelar
            </router-link>
            <button
              type="submit"
              :disabled="isSaving"
              class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60">
              {{ isSaving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </DefaultLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import ClassificationTypeResource from '@/api/resources/classificationType'
import GroupResource from '@/api/resources/group'
import SensorResource from '@/api/resources/sensor'

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const classificationTypes = ref<{ id: number; name: string }[]>([])
const sensors = ref<{ id: number; name: string }[]>([])
const sensorGroups = ref<{ id: number; name: string }[]>([])

const form = ref({
  name: '',
  linkMode: 'sensors' as 'sensors' | 'groups',
  classification_type_ids: [] as number[],
  sensor_ids: [] as number[],
  group_ids: [] as number[],
})

const errors = ref<Record<string, string>>({})
const saveError = ref('')
const isSaving = ref(false)

const isEdit = computed(() => route.name === 'classification-type-group-edit' && !!route.params.id)

const loadExistingGroup = async () => {
  const id = route.params.id
  if (!id || id === 'new') return
  try {
    const data = await ClassificationTypeResource.getGroup(Number(id))
    form.value = {
      name: data.name,
      linkMode: (data.sensors?.length ?? 0) > 0 ? 'sensors' : 'groups',
      classification_type_ids: data.classification_types?.map((ct) => ct.id) ?? [],
      sensor_ids: data.sensors?.map((s) => s.id) ?? [],
      group_ids: data.groups?.map((g) => g.id) ?? [],
    }
  } catch {
    router.replace({ name: 'alarms' })
  }
}

const fetchData = async () => {
  try {
    const [types, groups, sensorsList] = await Promise.all([
      ClassificationTypeResource.getTypes(),
      GroupResource.getGroups(),
      SensorResource.getAllSensors(),
    ])
    classificationTypes.value = Array.isArray(types) ? types : []
    sensorGroups.value = Array.isArray(groups) ? groups : []
    const sensorsRaw = Array.isArray(sensorsList) ? sensorsList : sensorsList?.data ?? []
    sensors.value = sensorsRaw.map((s: { id: number; name: string }) => ({ id: s.id, name: s.name }))
  } catch {
    classificationTypes.value = []
    sensorGroups.value = []
    sensors.value = []
  }
}

const save = async () => {
  errors.value = {}
  saveError.value = ''
  const trimmed = form.value.name.trim()
  if (!trimmed) {
    errors.value.name = 'Nome é obrigatório'
    return
  }
  if (form.value.classification_type_ids.length === 0) {
    errors.value.types = 'Selecione pelo menos um tipo de classificação'
    return
  }
  const hasSensors = form.value.linkMode === 'sensors' && form.value.sensor_ids.length > 0
  const hasGroups = form.value.linkMode === 'groups' && form.value.group_ids.length > 0
  if (!hasSensors && !hasGroups) {
    errors.value.link = 'Selecione pelo menos um sensor ou grupo de sensores'
    return
  }

  isSaving.value = true
  try {
    const params = {
      name: trimmed,
      classification_type_ids: form.value.classification_type_ids,
      sensor_ids: form.value.linkMode === 'sensors' ? form.value.sensor_ids : [],
      group_ids: form.value.linkMode === 'groups' ? form.value.group_ids : [],
    }
    if (isEdit.value) {
      await ClassificationTypeResource.updateGroup(Number(route.params.id), params)
    } else {
      await ClassificationTypeResource.createGroup(params)
    }
    router.push({ name: 'alarms' })
  } catch (e: any) {
    const msg = e?.response?.data?.errors
    saveError.value = Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar.'
  } finally {
    isSaving.value = false
  }
}

onMounted(async () => {
  isLoading.value = true
  await fetchData()
  if (isEdit.value) {
    await loadExistingGroup()
  } else {
    form.value = {
      name: '',
      linkMode: 'sensors',
      classification_type_ids: [],
      sensor_ids: [],
      group_ids: [],
    }
  }
  isLoading.value = false
})
</script>
